import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, ChevronRight, Gift, Wallet, Camera, HelpCircle, Star, Crown,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useToast } from "../components/Toast";
import MembershipModal from "../components/MembershipModal";

// Minimal WhatsApp icon — lucide doesn't ship a brand mark for WhatsApp,
// so we inline a clean SVG that matches the rest of our iconography.
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#25D366"
        d="M17.5 14.4c-.3-.15-1.7-.83-1.96-.93-.26-.1-.45-.15-.64.15-.19.3-.74.93-.9 1.12-.17.2-.33.22-.61.07-.28-.15-1.18-.44-2.25-1.38-.83-.74-1.4-1.66-1.56-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.33.42-.5.14-.16.19-.28.28-.47.09-.19.05-.35-.02-.5-.07-.15-.64-1.54-.88-2.1-.23-.55-.47-.48-.64-.49h-.55c-.19 0-.5.07-.76.35-.26.28-1 .98-1 2.39 0 1.41 1.03 2.77 1.17 2.96.14.19 2.02 3.09 4.9 4.33.68.29 1.22.47 1.64.6.69.22 1.31.19 1.8.11.55-.08 1.7-.7 1.94-1.36.24-.66.24-1.23.17-1.36-.07-.13-.26-.2-.55-.35zM12 21.5c-1.65 0-3.27-.44-4.68-1.27L3 21.5l1.29-4.24A9.5 9.5 0 1 1 12 21.5zM12 4.06a7.94 7.94 0 0 0-6.74 12.13l-.77 2.55 2.63-.69A7.94 7.94 0 1 0 12 4.06z"
      />
    </svg>
  );
}

// Diagonal restaurant photo strip used in the Earn Cash Back hero. Three
// equal-width brand-coloured tiles fill the right side fully, each showing
// an AI-generated cartoon food illustration. Subtle slanted seams between
// tiles give the "tilted receipts" feel without cropping any image.
function RestaurantCollage() {
  return (
    <div style={{
      position: "absolute", inset: 0, overflow: "hidden",
      borderTopRightRadius: 26, borderBottomRightRadius: 26,
      display: "flex",
    }}>
      <BrandTile bg="#C8161D" img="/hero/burger.png" rightSkew />
      <BrandTile bg="#D11A20" img="/hero/chicken_bucket.png" bothSkew />
      <BrandTile bg="#F2691B" img="/hero/chicken_tenders.png" leftSkew />
    </div>
  );
}

function BrandTile({ bg, img, leftSkew, rightSkew, bothSkew }) {
  // Slight horizontal skew on the inside edges only (right of first, both of
  // middle, left of last) — gives the diagonal "shingled" feel without
  // pushing the outermost tile content off-screen.
  let clipPath;
  if (rightSkew) clipPath = "polygon(0 0, 100% 0, 92% 100%, 0 100%)";
  else if (leftSkew) clipPath = "polygon(8% 0, 100% 0, 100% 100%, 0 100%)";
  else if (bothSkew) clipPath = "polygon(8% 0, 100% 0, 92% 100%, 0 100%)";
  return (
    <div style={{
      flex: 1, position: "relative",
      background: bg,
      marginLeft: leftSkew || bothSkew ? -6 : 0,
      marginRight: rightSkew || bothSkew ? -6 : 0,
      clipPath,
      overflow: "hidden",
    }}>
      <img
        src={img}
        alt=""
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          width: "100%", height: "100%",
          objectFit: "cover",
        }}
      />
    </div>
  );
}

// Decorative mini wheel used inside the Spin & Win card — removed with the card itself.

export default function Home() {
  const { user, refresh, requireAuth } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [unread, setUnread] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [showMembership, setShowMembership] = useState(false);
  const [promo, setPromo] = useState(() => {
    try {
      const cached = sessionStorage.getItem("cashback_promo_v1");
      if (cached) {
        const p = JSON.parse(cached);
        return p?.active && p?.text ? p : null;
      }
    } catch {}
    return null;
  });

  // Convenience wrappers that gate a navigation behind sign-up.
  const gatedNav = (path) => () => requireAuth(() => navigate(path));

  const loadUnread = useCallback(async () => {
    try { setUnread((await api.unreadCount()).count); } catch {}
  }, []);
  const loadPromo = useCallback(async () => {
    try {
      const p = await api.promo();
      const next = p?.active && p?.text ? p : null;
      setPromo(next);
      try {
        if (next) sessionStorage.setItem("cashback_promo_v1", JSON.stringify(next));
        else sessionStorage.removeItem("cashback_promo_v1");
      } catch {}
    } catch {}
  }, []);
  const loadTotalPaid = useCallback(async () => {
    try {
      const s = await api.platformStats();
      setTotalPaid(Number(s?.total_paid_gbp || 0));
    } catch {}
  }, []);

  useEffect(() => {
    loadTotalPaid();
    // Refresh the ticker every 30s so the counter keeps climbing while
    // the user has Home open — this is the "everytime another person
    // makes money update that total" requirement.
    const t = setInterval(loadTotalPaid, 30000);
    return () => clearInterval(t);
  }, [loadTotalPaid]);

  useEffect(() => {
    if (user) {
      refresh(); loadUnread(); loadPromo();
      const onVisible = () => {
        if (document.visibilityState === "visible") { refresh(); loadUnread(); loadPromo(); loadTotalPaid(); }
      };
      document.addEventListener("visibilitychange", onVisible);
      return () => document.removeEventListener("visibilitychange", onVisible);
    } else {
      // Guests can still see active promo banner — fetch it without auth.
      loadPromo();
    }
  }, [user, refresh, loadUnread, loadPromo, loadTotalPaid]);

  const balance = user?.balance ?? 0;

  return (
    <div className="fade-in" style={{ background: "var(--bg)", minHeight: "100vh", paddingBottom: 120 }}>
      <div style={{
        maxWidth: 560, margin: "0 auto",
        padding: "max(18px, env(safe-area-inset-top)) 16px 0",
      }}>
        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
            <button
              data-testid="wallet-balance-pill"
              onClick={gatedNav("/cashout")}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "var(--surface)", border: "1px solid var(--border)",
                borderRadius: 999, padding: "8px 16px 8px 8px",
                boxShadow: "0 10px 24px -16px rgba(11,36,71,0.22)",
                flex: 1, minWidth: 0,
              }}
            >
              <span style={{
                width: 36, height: 36, borderRadius: 999, flexShrink: 0,
                background: "var(--primary-soft)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Wallet size={17} color="var(--primary-dark)" />
              </span>
              <span data-testid="wallet-balance-text" style={{
                color: "var(--navy)", fontSize: 17, fontWeight: 800, letterSpacing: -0.2,
                whiteSpace: "nowrap",
              }}>
                £{balance.toFixed(2)}
              </span>
            </button>
          </div>

          <button
            data-testid="notif-bell"
            onClick={gatedNav("/inbox")}
            style={{
              position: "relative",
              width: 44, height: 44, borderRadius: 22, flexShrink: 0,
              background: "var(--surface)", border: "1px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 8px 22px -16px rgba(11,36,71,0.2)",
            }}
          >
            <Bell size={18} color="var(--primary)" />
            {unread > 0 && (
              <span style={{
                position: "absolute", top: -3, right: -3,
                minWidth: 18, height: 18, padding: "0 5px",
                borderRadius: 9, background: "var(--accent-pink)", color: "#FFFFFF",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 800,
                border: "2px solid var(--bg)",
              }}>
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </div>

        {/* WhatsApp channel CTA — full-width row */}
        <a
          data-testid="home-whatsapp-link"
          href="https://whatsapp.com/channel/0029VbCPSos2P59jZXjEhr01"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: "100%", marginTop: 14,
            display: "flex", alignItems: "center", gap: 12, textAlign: "left",
            background: "#128C7E", /* WhatsApp deep brand green */
            border: "1px solid #0E7060",
            borderRadius: 14, padding: "10px 14px 10px 12px",
            boxShadow: "0 14px 28px -20px rgba(18, 140, 126, 0.55)",
            textDecoration: "none",
            minWidth: 0,
          }}
        >
          <span style={{
            width: 34, height: 34, borderRadius: 999, flexShrink: 0,
            background: "#FFFFFF",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 14px -6px rgba(0,0,0,0.18)",
          }}>
            <WhatsAppIcon size={20} />
          </span>
          <span style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
            <span style={{
              display: "block", color: "#FFFFFF",
              fontSize: 13, fontWeight: 800,
            }}>
              Join us on WhatsApp
            </span>
            <span style={{
              display: "block", color: "rgba(255,255,255,0.9)",
              fontSize: 10.5, fontWeight: 600, marginTop: 1, lineHeight: 1.3,
            }}>
              Cashback drops, reminders &amp; bonus offers.
            </span>
          </span>
          <ChevronRight size={14} color="#FFFFFF" />
        </a>

        {/* Trustpilot review CTA — slim row, just enough to invite a tap */}
        <a
          data-testid="home-trustpilot-link"
          href="https://www.trustpilot.com/review/mrscashback.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            width: "100%", marginTop: 10,
            display: "flex", alignItems: "center", gap: 10, textAlign: "left",
            background: "#FFFFFF",
            border: "1px solid var(--peach-border)",
            borderRadius: 12, padding: "8px 12px",
            boxShadow: "0 10px 22px -22px rgba(11,36,71,0.18)",
            textDecoration: "none",
          }}
        >
          <span style={{
            width: 24, height: 24, borderRadius: 999, flexShrink: 0,
            background: "#00B67A", /* Trustpilot brand green */
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 10px -4px rgba(0,182,122,0.55)",
          }}>
            <Star size={12} color="#FFFFFF" strokeWidth={2.6} fill="#FFFFFF" />
          </span>
          <span style={{
            flex: 1, color: "var(--navy)",
            fontSize: 12.5, fontWeight: 800, lineHeight: 1.2,
          }}>
            Enjoying Cashback? Leave a review
          </span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            color: "var(--text-secondary)", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.2,
          }}>
            <span style={{ display: "inline-flex", gap: 1 }}>
              {[0,1,2,3,4].map((i) => (
                <Star key={i} size={10} fill="#00B67A" color="#00B67A" strokeWidth={0} />
              ))}
            </span>
            Trustpilot
          </span>
          <ChevronRight size={14} color="var(--text-secondary)" />
        </a>

        {/* Hero card: Earn Cash Back */}
        <div style={{
          marginTop: 14, display: "flex",
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 28, overflow: "hidden",
          boxShadow: "0 18px 36px -22px rgba(11,36,71,0.22)",
          minHeight: 280,
        }}>
          <div style={{ flex: "0 0 52%", padding: "24px 14px 22px 18px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div>
              <h2 style={{
                fontSize: 34, fontWeight: 900, letterSpacing: -1.3, margin: 0, lineHeight: 1.02,
                color: "var(--navy)",
              }}>
                Earn<br />
                <span style={{ color: "var(--primary)" }}>Cash Back</span>
              </h2>
              <p style={{
                color: "var(--text-secondary)", fontSize: 13.5, lineHeight: 1.45,
                marginTop: 12, marginBottom: 14,
              }}>
                Snap receipts from your favourite restaurants.
              </p>
            </div>
            <button
              data-testid="upload-receipt-button"
              className="btn-primary"
              onClick={gatedNav("/upload")}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                fontSize: 13.5, fontWeight: 800, padding: "14px 16px",
              }}
            >
              <Camera size={16} />
              Upload Receipt
              <ChevronRight size={16} />
            </button>
          </div>
          <div style={{ flex: "0 0 48%", position: "relative", overflow: "hidden" }}>
            <RestaurantCollage />
          </div>
        </div>

        {/* Refer a friend (compact) + How it works — 2-col row */}
        <div style={{
          display: "grid", gridTemplateColumns: "70% 1fr", gap: 10,
          marginTop: 14, alignItems: "stretch",
        }}>
          <button
            data-testid="home-refer-card"
            onClick={gatedNav("/refer")}
            style={{
              background: "var(--accent-pink)",
              border: "1px solid var(--accent-pink)",
              borderRadius: 18, padding: "12px 14px", textAlign: "left",
              boxShadow: "0 14px 28px -20px rgba(230, 59, 90, 0.55)",
              display: "flex", alignItems: "center", gap: 10,
              position: "relative", overflow: "hidden",
              minWidth: 0,
            }}
          >
            {/* Tiny confetti sparkles for friendliness */}
            <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
              {[
                { c: "#FFFFFF", t: 8, l: "40%", s: 3 },
                { c: "#FFC72C", t: 20, l: "78%", s: 4 },
                { c: "#FFFFFF", t: 48, l: "62%", s: 3 },
              ].map((d, i) => (
                <span key={i} style={{
                  position: "absolute", top: d.t, left: d.l,
                  width: d.s, height: d.s, borderRadius: 999, background: d.c, opacity: 0.85,
                }} />
              ))}
            </div>
            <span style={{
              width: 40, height: 40, borderRadius: 12, flexShrink: 0,
              background: "rgba(255,255,255,0.22)",
              border: "1px solid rgba(255,255,255,0.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              position: "relative",
            }}>
              <Gift size={20} color="#FFFFFF" />
            </span>
            <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
              <div style={{ fontWeight: 800, fontSize: 12.5, color: "#FFFFFF", lineHeight: 1.15 }}>
                Refer a friend
              </div>
              <div style={{ color: "#FFFFFF", fontWeight: 900, fontSize: 14, marginTop: 2, lineHeight: 1.1 }}>
                earn up to £5 each
              </div>
              <div style={{ color: "rgba(255,255,255,0.88)", fontSize: 10, marginTop: 3, lineHeight: 1.35 }}>
                When their first receipt is approved.
              </div>
            </div>
            <span style={{
              background: "#FFFFFF", color: "var(--accent-pink)",
              borderRadius: 999, padding: "6px 10px",
              display: "inline-flex", alignItems: "center", gap: 3,
              fontSize: 10.5, fontWeight: 800, flexShrink: 0,
              boxShadow: "0 6px 14px -8px rgba(0,0,0,0.25)",
              position: "relative",
            }}>
              Refer <ChevronRight size={11} />
            </span>
          </button>

          <button
            data-testid="home-how-it-works-link"
            onClick={() => navigate("/how-it-works")}
            style={{
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 5,
              background: "var(--burgundy)",
              border: "1px solid var(--burgundy)",
              borderRadius: 18, padding: "12px 10px",
              boxShadow: "0 14px 28px -20px rgba(122, 26, 46, 0.55)",
              textAlign: "center",
            }}
          >
            <span style={{
              width: 28, height: 28, borderRadius: 999, flexShrink: 0,
              background: "var(--primary)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 6px 14px -6px rgba(247,122,42,0.7)",
            }}>
              <HelpCircle size={14} color="#FFFFFF" />
            </span>
            <span style={{
              color: "#FFFFFF", fontSize: 11.5, fontWeight: 800, lineHeight: 1.15,
            }}>
              How it<br />works
            </span>
          </button>
        </div>

        {/* Total paid on platform — Stripe-style inline live counter (no box) */}
        <div
          data-testid="home-total-paid-card"
          style={{
            width: "100%", marginTop: 18, padding: "4px 4px 2px",
            display: "flex", alignItems: "baseline", flexWrap: "wrap",
            columnGap: 8, rowGap: 2, textAlign: "left",
          }}
        >
          <span style={{
            color: "var(--navy)", opacity: 0.7,
            fontSize: 13, fontWeight: 600, letterSpacing: -0.1,
          }}>
            Total earned on our platform:
          </span>
          <span
            data-testid="home-total-paid-value"
            style={{
              color: "var(--navy)",
              fontSize: 15, fontWeight: 800, letterSpacing: -0.2,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            £{totalPaid.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* Small discreet membership pill — bottom-right, above the bottom nav.
          Opens a modal explaining tier boosts and how to upgrade. */}
      <button
        data-testid="home-membership-pill"
        onClick={() => setShowMembership(true)}
        style={{
          position: "fixed",
          right: 14, bottom: 82,
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "var(--navy)",
          border: "1px solid rgba(255,255,255,0.15)",
          color: "#FFFFFF",
          borderRadius: 999,
          padding: "7px 12px 7px 9px",
          fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
          boxShadow: "0 10px 24px -12px rgba(11, 36, 71, 0.55)",
          cursor: "pointer",
          zIndex: 40,
        }}
      >
        <span style={{
          width: 20, height: 20, borderRadius: 999,
          background: "#F5C84A",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <Crown size={11} color="#0B2447" strokeWidth={2.6} />
        </span>
        Membership
      </button>

      <MembershipModal
        open={showMembership}
        onClose={() => setShowMembership(false)}
      />

    </div>
  );
}
